import React from 'react';
import { useSelector } from 'react-redux';
import DimensionCombination from './DimensionCombination';
import AddDimensionPopup from '../AddDimensionPopup';
import {
  DimensionTitle,
  DimensionWrapper,
  ListWrapper,
  PointerIconRing,
  ScanTitle,
} from '../styled';
import ChevronIcon from '@src/icons/aiAlerts/right-chevron.svg';
import {
  getCardinalityText,
  isAdvanceCustomerType,
  isFirstDimension,
  shouldDisplayAddDim,
  transformDimensionList,
} from '../utils';
import { BACKEND_NAME_SEPARATOR, NAME_SEPARATOR, MAX_ALLOWED_LEVEL } from '../constants';
import {
  aiAlertFusionVsiSelector,
  aiAlertsDimensionMappingSelector,
} from '@store/common/selectors';
import { isAppModule } from '@src/utils/constants';
import { VSI_AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT } from '@src/utils/aiAlerts/mapping';
import { Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import featureNames, { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useFeature } from '@src/globalStore/featureStore';

const product = window.conviva.module || 'EI';

export default function DimensionGroup({ aiConfigList, customerType }) {
  const aiAlertsCustomDimensionsUpperLimitFF = useFeature(
    featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT,
  );
  const vsiAIAlertsCustomDimensionsUpperLimit =
    aiAlertsCustomDimensionsUpperLimitFF + VSI_AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT;
  const dpiAIAlertsCustomDimensionsPerMetricLimit = useFeature(
    featureNames.DPI_AI_ALERTS_CUSTOM_DIMENSIONS_PER_METRIC_LIMIT,
  );
  const maxCount: number = isAppModule
    ? dpiAIAlertsCustomDimensionsPerMetricLimit
    : vsiAIAlertsCustomDimensionsUpperLimit;

  const aiDimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const aiDimensionMappingForProduct = aiDimensionMapping[product.toLocaleLowerCase()];
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);
  const aiAlertFusionVsiOn = useSelector(aiAlertFusionVsiSelector);

  // Single dimension selection logic (works for both VSI/EI and DPI)
  const singleDimensionList = (level) =>
    transformDimensionList(aiDimensionMappingForProduct, aiConfigList, level, aiAlertFusionVsiOn);

  // Resolve a (possibly multi-dim) backendName like "CDN&&&COUNTRY" to a display
  // string built from `pulse5Label`s, e.g. "CDN, Country". This is the same source
  // the single-dimension dropdown uses (see transformDimensionList → value: pulse5Label),
  // so the two dropdowns in AddDimensionPopup render the same label for the same
  // underlying dimension. Falls back to the persisted `name` (or the raw token)
  // when a mapping entry is missing.
  const resolveDisplayName = (backendName: string, fallbackName: string): string => {
    if (!backendName) return fallbackName;
    const parts = backendName.split(BACKEND_NAME_SEPARATOR);
    const resolved = parts.map((bn) => aiDimensionMappingForProduct?.[bn]?.pulse5Label);
    if (resolved.some((label) => !label)) {
      return fallbackName;
    }
    return resolved.join(NAME_SEPARATOR);
  };

  const generatePreLevelDimensionOptions = (level) => {
    if (level === 1) {
      return [];
    }
    // the candidate dimension combination for the current level
    const preLevelData = aiConfigList.find((levelData) => levelData.level === level - 1);
    const dimensionList = preLevelData?.dimensionData || [];

    const filteredDimensionList = dimensionList.filter((curDimension, index) => {
      const lastIndex = dimensionList.findLastIndex(
        (lastDimension) => lastDimension.name === curDimension.name,
      );
      if (lastIndex === index) {
        return true;
      }

      // For DPI (all custom dimensions), just keep the last occurrence
      // For VSI/EI, prefer standard dimensions over custom ones
      if (isAppModule) {
        return false; // Keep only the last occurrence (already handled above)
      } else {
        // VSI/EI: select standard one first
        return !dimensionList[index].isCustomDimension;
      }
    });

    return filteredDimensionList.map((item) => {
      const displayName = resolveDisplayName(item.backendName, item.name);
      return {
        value: displayName,
        backendName: item.backendName,
        label: displayName.split(NAME_SEPARATOR).join(`${NAME_SEPARATOR} `),
      };
    });
  };

  const displayAddDimensionBtn =
    isAdvanceCustomerType(customerType) || (isAppModule && enableDPICustomDimension);

  return aiConfigList
    .filter(({ level }) => level > 0)
    .map((currentLevel) => {
      const { level, totalCombinations, dimensionData } = currentLevel;
      const isSingleDimension = isFirstDimension(level);
      const dimensionText = `${isSingleDimension ? 'Single ' : `${level}-`}Dimension`;

      const prevLevel = aiConfigList.find((item) => item.level === level - 1);

      // in DPI skip rendering if no data in current and previous level
      // in VSI skip rendering if no data in current level
      if (
        isAppModule && enableDPICustomDimension
          ? !dimensionData?.length &&
            (!prevLevel?.dimensionData?.length || level === MAX_ALLOWED_LEVEL)
          : !dimensionData?.length
      ) {
        return null;
      }

      return (
        <li key={level}>
          <ListWrapper>
            <PointerIconRing isSubPointer margin="0 12px 0 0">
              <ChevronIcon />
            </PointerIconRing>
            <div>
              <DimensionTitle>{dimensionText}</DimensionTitle>
              <ScanTitle>(Scanning {getCardinalityText(totalCombinations, 2)})</ScanTitle>
            </div>
          </ListWrapper>
          <DimensionWrapper displayAddDimension={displayAddDimensionBtn}>
            {level < MAX_ALLOWED_LEVEL &&
              shouldDisplayAddDim(displayAddDimensionBtn, aiConfigList, maxCount) && (
                <AddDimensionPopup
                  level={level}
                  dimensionText={dimensionText}
                  singleDimensionOptions={singleDimensionList(level)}
                  preLevelDimensionOptions={generatePreLevelDimensionOptions(level)}
                  dimensionData={dimensionData}
                  aiConfigList={aiConfigList}
                />
              )}
            {dimensionData?.length > 0 ? (
              <DimensionCombination
                level={level}
                aiConfigList={aiConfigList}
                combinations={dimensionData}
              />
            ) : (
              <Alert
                style={{ height: '35px', top: '10px' }}
                message={
                  <>
                    There are no dimension combinations added for this level. Please Add using{' '}
                    <PlusOutlined style={{ margin: '0 4px' }} /> button
                  </>
                }
                type="info"
                showIcon
              />
            )}
          </DimensionWrapper>
        </li>
      );
    });
}
